import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package,
  ArrowLeft,
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getSkuImports, InventoryItem } from "core/Services/InventoryServices";
import { USER_ID } from "utils/constant";

const MyInventorySkuDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sku } = useParams<{ sku: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [importRecords, setImportRecords] = useState<InventoryItem[]>([]);
  const [skuInfo, setSkuInfo] = useState<{
    product_name: string;
    product_code: string;
    price: number;
    manufacturer: string | null;
  } | null>(null);

  const merchantId = localStorage.getItem(USER_ID);

  useEffect(() => {
    const fetchSkuImports = async () => {
      if (!merchantId || !sku) return;

      setIsLoading(true);
      try {
        const response = await getSkuImports(parseInt(merchantId), sku);
        setImportRecords(Array.isArray(response.data) ? response.data : []);

        // Get SKU info from first record
        if (response.data.length > 0) {
          const firstRecord = response.data[0];
          setSkuInfo({
            product_name: firstRecord.product_name,
            product_code: firstRecord.product_code,
            price: firstRecord.price,
            manufacturer: firstRecord.manufacturer,
          });
        }
      } catch (error) {
        console.error("Error fetching SKU imports:", error);
        toast.error(
          t("inventory.errorFetchingImports") || "Error fetching import records"
        );
        setImportRecords([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSkuImports();
  }, [merchantId, sku, t]);

  const getStatusColors = (status: string) => {
    switch (status) {
      case "received":
        return {
          bg: "bg-green-50",
          border: "border-green-200",
          text: "text-green-800",
          icon: <CheckCircle size={16} className="text-green-600" />,
          label: t("inventory.status_received") || "Received",
        };
      case "missing_quantity":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          text: "text-red-800",
          icon: <XCircle size={16} className="text-red-600" />,
          label: t("inventory.status_missing_quantity") || "Missing Quantity",
        };
      case "under_review":
      default:
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          text: "text-yellow-800",
          icon: <AlertCircle size={16} className="text-yellow-600" />,
          label: t("inventory.status_under_review") || "Under Review",
        };
    }
  };

  const calculateDisplayQuantity = (record: InventoryItem) => {
    // The 'quantity' field now represents current available quantity
    // It's already calculated correctly in the backend
    if (record.quantity_status === "under_review") {
      return 0;
    }
    return record.quantity;
  };

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <button
              onClick={() => navigate("/my-inventory")}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>
                {t("inventory.backToDashboard") || "Back to Dashboard"}
              </span>
            </button>

            <div className="flex items-center gap-3">
              <Package size={32} className="text-primary" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {t("inventory.skuImportRecords") || "Import Records"}: {sku}
                </h1>
                {skuInfo && (
                  <div className="mt-2 text-sm text-gray-600">
                    <p className="font-medium">{skuInfo.product_name}</p>
                    <p className="text-xs">
                      {t("inventory.productCode")}: {skuInfo.product_code}
                      {skuInfo.manufacturer && (
                        <>
                          {" "}
                          | {t("inventory.manufacturer")}:{" "}
                          {skuInfo.manufacturer}
                        </>
                      )}{" "}
                      | {t("inventory.price")}: JOD {skuInfo.price.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner - Read Only Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle
            className="text-blue-600 flex-shrink-0 mt-0.5"
            size={20}
          />
          <div className="text-sm text-blue-800">
            <p className="font-medium">
              {t("inventory.readOnlyImportView") || "View-only mode"}
            </p>
            <p className="mt-1">
              {t("inventory.contactAdminForChanges") ||
                "You can view all import records and their status. Contact your administrator to update import statuses or manage inventory."}
            </p>
          </div>
        </div>

        {/* Import Records */}
        {isLoading ? (
          <div className="space-y-4">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <Skeleton height={100} />
                </div>
              ))}
          </div>
        ) : importRecords.length > 0 ? (
          <div className="space-y-4">
            {importRecords.map((record, index) => {
              const statusConfig = getStatusColors(record.quantity_status);
              const displayQuantity = calculateDisplayQuantity(record);

              return (
                <div
                  key={record.id}
                  className={`border-2 ${statusConfig.border} ${statusConfig.bg} rounded-lg p-6 transition-all`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full ${statusConfig.bg} border-2 ${statusConfig.border} flex items-center justify-center font-bold ${statusConfig.text}`}
                      >
                        #{index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {t("inventory.import") || "Import"} #{index + 1}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t("inventory.batchId")}:{" "}
                          {record.import_batch_id?.substring(0, 8)}...
                        </p>
                      </div>
                    </div>
                    <div
                      className={`flex items-center gap-2 px-3 py-1 rounded-full border-2 ${statusConfig.border} ${statusConfig.bg}`}
                    >
                      {statusConfig.icon}
                      <span
                        className={`font-medium text-sm ${statusConfig.text}`}
                      >
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Original Quantity */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">
                        {t("inventory.originalQuantity") || "Original Quantity"}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {record.original_quantity}
                      </p>
                    </div>

                    {/* Display Quantity */}
                    <div
                      className={`p-4 rounded-lg border-2 ${
                        displayQuantity === 0
                          ? "bg-gray-50 border-gray-200"
                          : "bg-green-50 border-green-200"
                      }`}
                    >
                      <p
                        className="text-xs font-medium mb-1"
                        style={{
                          color: displayQuantity === 0 ? "#6B7280" : "#166534",
                        }}
                      >
                        {t("inventory.availableQuantity") ||
                          "Available Quantity"}
                      </p>
                      <p
                        className="text-2xl font-bold"
                        style={{
                          color: displayQuantity === 0 ? "#6B7280" : "#15803D",
                        }}
                      >
                        {displayQuantity}
                      </p>
                    </div>

                    {/* Missing Quantity (if applicable) */}
                    {record.quantity_status === "missing_quantity" && (
                      <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                        <p className="text-xs text-red-600 font-medium mb-1">
                          {t("inventory.missingQuantity") || "Missing Quantity"}
                        </p>
                        <p className="text-2xl font-bold text-red-800">
                          {record.missing_quantity_number || 0}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Import Details */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-2">
                      <Calendar size={16} className="text-gray-500 mt-1" />
                      <div>
                        <p className="text-xs text-gray-600">
                          {t("inventory.importDate") || "Import Date"}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {record.import_date
                            ? new Date(record.import_date).toLocaleDateString()
                            : "-"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <User size={16} className="text-gray-500 mt-1" />
                      <div>
                        <p className="text-xs text-gray-600">
                          {t("inventory.importedBy") || "Imported By"}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {record.imported_by?.name || "-"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Package size={16} className="text-gray-500 mt-1" />
                      <div>
                        <p className="text-xs text-gray-600">
                          {t("inventory.price") || "Price"}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          JOD {record.price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {record.note && (
                    <div className="mt-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <p className="text-xs text-yellow-800 font-medium mb-1">
                        📝 {t("inventory.notes") || "Notes"}
                      </p>
                      <p className="text-sm text-gray-700">{record.note}</p>
                    </div>
                  )}

                  {/* Status Explanation */}
                  {record.quantity_status === "under_review" && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle
                        size={16}
                        className="text-yellow-600 flex-shrink-0 mt-0.5"
                      />
                      <p className="text-sm text-yellow-800">
                        {t("inventory.underReviewExplanation") ||
                          "This import is pending approval. Quantity will be available once approved by an administrator."}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Package size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">
              {t("inventory.noImportRecords") || "No import records found"}
            </p>
            <p className="text-sm mt-2">
              {t("inventory.noImportsForSku") ||
                "There are no import records for this SKU."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyInventorySkuDetails;
